import type { StateStorageStrategy } from "@snowplow/browser-tracker"

/** Whether the browser lets the page use `localStorage`. */
function canUseLocalStorage(): boolean {
  try {
    const key = "__snowplow_storage_probe__"

    globalThis.localStorage.setItem(key, "1")
    globalThis.localStorage.removeItem(key)

    return true
  } catch {
    return false
  }
}

/** Whether the browser keeps first-party cookies set by the page. */
function canUseCookies(): boolean {
  try {
    const probe = "__snowplow_cookie_probe__"

    globalThis.document.cookie = `${probe}=1; path=/; SameSite=Lax`

    const works = globalThis.document.cookie.includes(`${probe}=1`)

    globalThis.document.cookie = `${probe}=; path=/; max-age=0; SameSite=Lax`

    return works
  } catch {
    return false
  }
}

/**
 * Where Snowplow keeps its state (visitor id, session) when it's set up.
 *
 * Its default (`cookieAndLocalStorage`) logs a console error on every page
 * load when the browser blocks storage (Safari "Block All Cookies", Chrome
 * blocking site data, some in-app browsers). So: the default while
 * `localStorage` works, cookies alone when it's blocked, and none (nothing
 * stored, so a new visitor id per page load) when cookies are blocked too.
 *
 * `cookies`, `localStorage`: the checks, for tests.
 */
export function chooseSnowplowStateStorage({
  cookies = canUseCookies,
  localStorage = canUseLocalStorage,
}: {
  cookies?: () => boolean
  localStorage?: () => boolean
} = {}): StateStorageStrategy {
  if (localStorage()) return "cookieAndLocalStorage"

  return cookies() ? "cookie" : "none"
}
